export type DateRangeKey =
  | 'today'
  | 'yesterday'
  | 'previous_7_days'
  | 'previous_30_days'
  | 'previous_week'
  | 'previous_month'
  | 'previous_year'
  | 'this_week'
  | 'this_month'
  | 'this_year'
  | 'custom';

export const DATE_RANGE_LABELS: Record<DateRangeKey, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  previous_7_days: 'Previous 7 days',
  previous_30_days: 'Previous 30 days',
  previous_week: 'Previous week',
  previous_month: 'Previous month',
  previous_year: 'Previous year',
  this_week: 'This week',
  this_month: 'This month',
  this_year: 'This year',
  custom: 'Custom range',
};

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDateRange(key: DateRangeKey): { startDate: string; endDate: string } {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  let startDate: Date;
  let endDate: Date;

  switch (key) {
    case 'today': {
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'yesterday': {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'previous_7_days': {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 7);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'previous_30_days': {
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'previous_week': {
      // Monday of last week
      const dayOfWeek = today.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diffToMonday - 7);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'previous_month': {
      startDate = new Date(currentYear, currentMonth - 1, 1);
      endDate = new Date(currentYear, currentMonth, 0); // last day of prev month
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'previous_year': {
      startDate = new Date(currentYear - 1, 0, 1);
      endDate = new Date(currentYear - 1, 11, 31);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'this_week': {
      const dow = today.getDay();
      const diffMon = dow === 0 ? 6 : dow - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diffMon);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'this_month': {
      startDate = new Date(currentYear, currentMonth, 1);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'this_year': {
      startDate = new Date(currentYear, 0, 1);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;
    }

    case 'custom':
    default:
      // Default to previous 30 days
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
  }

  return {
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
  };
}
